import { flow } from "fp-ts/lib/function";
import { type MessageDescriptor } from "react-intl";

import { SurfaceType } from "../../gql-generated/types";
import {
    getGameLensPreviewValidationMessage,
    validateGameLensPreview as validateSharedGameLensPreview,
} from "../../lens-preview/validation";
import { type LensPreviewMediaItem } from "../../shared/state";

import { messages } from "./messages.generated";

export interface SubmissionValidationResult {
    isValid: boolean;
    errorMessage?: MessageDescriptor;
}

const validatePreview =
    (surfaceType: readonly SurfaceType[], previewMediaItems: LensPreviewMediaItem[]) =>
    (result: SubmissionValidationResult): SubmissionValidationResult => {
        if (!result.isValid) return result;
        const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
        if (isSpectaclesOnly && previewMediaItems.some((item) => item.isVideo)) {
            return {
                isValid: false,
                errorMessage: messages.spectaclesVideoPreviewError,
            };
        }
        return result;
    };

const validateGameLensPreview =
    (
        surfaceType: readonly SurfaceType[],
        previewMediaItems: LensPreviewMediaItem[],
        isGameUserProvided: boolean,
        previewValidationMessage?: string | null,
    ) =>
    (result: SubmissionValidationResult): SubmissionValidationResult => {
        if (!result.isValid) return result;

        const hasSnapchatSurface = surfaceType.includes(SurfaceType.SurfaceTypeSnapchat);

        if (!isGameUserProvided || !hasSnapchatSurface) {
            return result;
        }

        const gameLensPreviewValidationError = validateSharedGameLensPreview({
            previewMediaItems,
            previewValidationMessage,
        });
        const errorMessage = getGameLensPreviewValidationMessage(gameLensPreviewValidationError);

        if (errorMessage) {
            return {
                isValid: false,
                errorMessage,
            };
        }

        return result;
    };

export const areSubmissionDetailsValid = (
    surfaceType: readonly SurfaceType[],
    previewMediaItems: LensPreviewMediaItem[],
    isGameUserProvided = false,
    previewValidationMessage?: string | null,
): SubmissionValidationResult =>
    flow(
        validateGameLensPreview(surfaceType, previewMediaItems, isGameUserProvided, previewValidationMessage),
        validatePreview(surfaceType, previewMediaItems),
    )({ isValid: true });
